import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import Login from "@/pages/Login";
import OrganizationsList from "@/pages/Organizations/List";
import OrganizationForm from "@/pages/Organizations/Form";
import PublicationHousesList from "@/pages/PublicationHouses/List";
import PublicationHouseForm from "@/pages/PublicationHouses/Form";
import PeriodicalsList from "@/pages/Periodicals/List";
import PeriodicalForm from "@/pages/Periodicals/Form";
import SubscriptionPlansList from "@/pages/SubscriptionPlans/List";
import SubscriptionPlanForm from "@/pages/SubscriptionPlans/Form";
import SubscribersList from "@/pages/Subscribers/List";
import SubscriberForm from "@/pages/Subscribers/Form";
import SubscriberTypeChooser from "@/pages/Subscribers/Chooser";
import RenewalsDashboard from "@/pages/Renewals/Dashboard";
import BulkRenew from "@/pages/Renewals/BulkRenew";
import DispatchHome from "@/pages/Dispatch/Home";
import DispatchLabels from "@/pages/Dispatch/Labels";
import DispatchRTO from "@/pages/Dispatch/RTO";
import DispatchTrackingImport from "@/pages/Dispatch/TrackingImport";
import DispatchRedispatchQueue from "@/pages/Dispatch/RedispatchQueue";
import PublicRenew from "@/pages/PublicRenew";
import UsersList from "@/pages/Users/List";

function RoleRoute({ minRole, children }) {
  const { user, hasRole } = useAuth();
  if (user === null) return null;
  if (!hasRole(minRole)) {
    return (
      <div className="rounded-sm border border-rose-200 bg-rose-50 p-6 text-center text-sm text-rose-700" data-testid="route-forbidden">
        You do not have permission to access this page.
      </div>
    );
  }
  return children;
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-sm text-slate-500">Checking session…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/r/:token" element={<PublicRenew />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/organizations" replace />} />
        <Route path="organizations" element={<OrganizationsList />} />
        <Route path="organizations/new" element={<OrganizationForm />} />
        <Route path="organizations/:id/edit" element={<OrganizationForm />} />
        <Route path="publication-houses" element={<PublicationHousesList />} />
        <Route path="publication-houses/new" element={<PublicationHouseForm />} />
        <Route path="publication-houses/:id/edit" element={<PublicationHouseForm />} />
        <Route path="periodicals" element={<PeriodicalsList />} />
        <Route path="periodicals/new" element={<PeriodicalForm />} />
        <Route path="periodicals/:id/edit" element={<PeriodicalForm />} />
        <Route path="subscription-plans" element={<SubscriptionPlansList />} />
        <Route path="subscription-plans/new" element={<SubscriptionPlanForm />} />
        <Route path="subscription-plans/:id/edit" element={<SubscriptionPlanForm />} />
        <Route path="subscribers" element={<SubscribersList />} />
        <Route path="subscribers/new" element={<SubscriberTypeChooser />} />
        <Route path="subscribers/new/:type" element={<SubscriberForm />} />
        <Route path="subscribers/:id/edit" element={<SubscriberForm />} />
        <Route path="renewals" element={<RenewalsDashboard />} />
        <Route path="renewals/bulk" element={<BulkRenew />} />
        <Route path="dispatch" element={<DispatchHome />} />
        <Route path="dispatch/labels" element={<DispatchLabels />} />
        <Route path="dispatch/rto" element={<DispatchRTO />} />
        <Route path="dispatch/tracking-import" element={<DispatchTrackingImport />} />
        <Route path="dispatch/redispatch-queue" element={<DispatchRedispatchQueue />} />
        <Route path="users" element={<RoleRoute minRole="superadmin"><UsersList /></RoleRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
